import type { UpgradeParams } from "@/types/tile";
import { type TileIndex, type TileNeighbour } from "@/types/tile";
import { FrameName, tilerepoSettlementEmptyFrameNames } from "../config";
import TileEntity from "../TileEntity";

type HousesFrameNames =
	| FrameName.SMALL_HOUSES
	| FrameName.MEDIUM_HOUSES
	| FrameName.LARGE_HOUSES;

class SmallHousesBuildingTileEntity extends TileEntity {
	public static readonly frameName: HousesFrameNames = FrameName.SMALL_HOUSES;

	public get canUpgradeTo(): Set<FrameName> {
		if (this.cachedCanUpgradeTo) return this.cachedCanUpgradeTo;

		const upgrades = new Set<FrameName>([...super.canUpgradeTo]);
		if (!this.upgrades.size) return (this.cachedCanUpgradeTo = upgrades);

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				2,
				true,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null,
			) as Array<TileNeighbour>;

		if (neighbours.length === 0)
			return (this.cachedCanUpgradeTo = upgrades);

		if (
			neighbours.find(
				({ frameName }): boolean =>
					frameName === FrameName.CARPENTRY_DISTRICT,
			)
		)
			upgrades.delete(FrameName.CARPENTRY_DISTRICT);

		if (
			neighbours.find(
				({ frameName }): boolean =>
					frameName === FrameName.STONEMASONRY_DISTRICT,
			)
		)
			upgrades.delete(FrameName.STONEMASONRY_DISTRICT);

		return (this.cachedCanUpgradeTo = upgrades);
	}

	public get upgrades(): Map<FrameName, UpgradeParams> {
		if (this.cachedUpgrades) return this.cachedUpgrades;

		const upgrades = new Map<FrameName, UpgradeParams>(
			super.config.upgrades,
		);

		const [tileId0] = this.tilerepo.getTileIdsByTileIndex(this.index)!;
		const frameName0 = this.tilerepo.getFrameNameByTileId(tileId0)!;
		if (!tilerepoSettlementEmptyFrameNames.has(frameName0)) {
			return (this.cachedUpgrades = new Map<FrameName, UpgradeParams>([
				[FrameName.NOT_SET, upgrades.get(FrameName.NOT_SET)!],
			]));
		}

		return (this.cachedUpgrades = upgrades);
	}
}

export default SmallHousesBuildingTileEntity;
